import { useQuery } from "react-query";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { useAuth } from "@/contexts/AuthContext";

/** Export for use when invalidating after create/update/delete/reactivate */
export const BRANCHES_ACTIVE_QUERY_KEY = ["branches", "active"] as const;

/**
 * Shared hook for fetching active branches. Uses React Query cache so multiple
 * components (AppLayout, RegistrationApprovalPage, CatalogManagementPage, etc.)
 * share the same request and avoid duplicate /api/v1/branches?includeInactive=false calls.
 */
export function useActiveBranches(options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const isStaffOrAdminOrLibrarian =
    user?.isStaff ||
    user?.memberships?.some(
      (m) =>
        m.roleName === "Tenant Admin" ||
        m.role === "TENANT_ADMIN" ||
        m.roleName === "Librarian" ||
        m.role === "LIBRARIAN"
    );

  const enabled = options?.enabled ?? (!!user && isStaffOrAdminOrLibrarian);

  return useQuery<Branch[]>(BRANCHES_ACTIVE_QUERY_KEY, () => branchesApi.getActiveBranches(), {
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour - invalidate on create/update/delete/reactivate
  });
}
